<template>
  <div
    v-if="lines.length > 0"
    :class="['alert', 'alert-danger', centered ? 'text-center' : '']"
    role="alert">
    <!-- Os segmentos ficam colados de propósito: qualquer quebra de linha entre
         eles viraria um espaço no texto renderizado, e o telefone aparece no meio
         da frase ("fale com o suporte: (48) 3332-8503."). -->
    <p
      v-for="(segments, index) in lines"
      :key="index"
      class="mb-0"><template v-for="(segment, i) in segments"><a
        v-if="segment.link"
        :key="'link-' + i"
        :href="supportUrl"
        target="_blank"
        rel="noopener"
        class="link">{{ supportPhone }}</a><span
        v-else
        :key="'text-' + i">{{ segment.text }}</span></template></p>
    <a
      v-if="resolved.support"
      :href="supportUrl"
      target="_blank"
      rel="noopener"
      class="link">
      Clique aqui e fale com nosso suporte via WhatsApp
    </a>
  </div>
</template>

<script>
import {
  interpolate,
  normalizeError,
  SUPPORT_PHONE,
  SUPPORT_PLACEHOLDER,
  SUPPORT_WHATSAPP_URL
} from '../../utils/error-message'

// Sinais de que o objeto veio do transporte (axios / LoopBack / billing) em vez
// de ter sido montado pela tela. Serve de rede de segurança: se alguma tela
// ainda não migrada passar o erro cru, normalizamos aqui em vez de renderizar a
// mensagem em inglês do backend.
function isTransportError (error) {
  return Boolean(error.response || error.config || error.isAxiosError ||
    error.statusCode || error.status)
}

/**
 * Exibe o erro já normalizado por `utils/error-message` (TEL-2243).
 *
 * Renderiza com `{{ }}`, nunca com `v-html`. Isso é o que fecha a superfície de
 * XSS que existia quando a mensagem vinha do backend ou de um CRM de terceiro e
 * era injetada como HTML. Quando o texto precisa de link (suporte) ou de várias
 * linhas (validação multi-campo), quem monta é o template — a string em si
 * continua sendo só texto.
 *
 * `context` preenche os `{placeholder}` do mapa com dado que a própria tela tem,
 * porque o backend não manda esses valores separados. Exemplo:
 *
 *   <ErrorAlert :error="error" :context="{ ramal: form.ramal }" />
 *
 * `{suporte}` é o único marcador tratado aqui em vez de pelo `context`: ele vira
 * o link do WhatsApp. É opt-in — frase que cite o telefone sem o marcador
 * continua texto puro, para o link nunca aparecer onde ninguém pediu.
 */
export default {
  name: 'ErrorAlert',
  props: {
    // Aceita `Error` além de objeto simples: a rede de segurança recebe o erro
    // cru do axios, que é instância de Error e reprovaria num `type: Object` —
    // enchendo o console de aviso do Vue justamente quando algo já deu errado.
    //
    // NÃO aceita `String`, e isso é contrato, não esquecimento. O componente
    // decide o que renderizar pela FORMA do erro (ver `resolved`), e uma string
    // solta não diz se é texto da tela, mensagem de backend ou erro de
    // transporte — justamente a distinção que o TEL-2243 existe para manter.
    //
    // O preço é conhecido: passar string faz a caixa não renderizar NADA, em
    // silêncio. Foi assim que cinco telas de CRM ficaram mudas na rodada 2 da
    // review. Tela que guarda texto próprio em `this.error` deve envolver num
    // objeto — `{ message: 'texto em pt-BR' }` — em vez de passar a string.
    error: {
      type: [Object, Error],
      default: null
    },
    context: {
      type: Object,
      default: null
    },
    centered: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    supportUrl () {
      return SUPPORT_WHATSAPP_URL
    },
    supportPhone () {
      return SUPPORT_PHONE
    },
    /**
     * Aceita três formatos, nesta ordem:
     *
     * 1. `{ lines, support }` — saída do normalizador. É o caminho normal.
     * 2. Erro cru do transporte — normaliza aqui. Rede de segurança para tela
     *    que ainda não migrou: garante que nenhum texto de backend chegue à tela
     *    mesmo se um ponto de atribuição tiver passado batido.
     * 3. `{ message }` — as ~105 mensagens escritas pela própria tela, em pt-BR.
     *    Essas não vêm do backend e continuam valendo como estão.
     */
    resolved () {
      const error = this.error
      if (!error) return { lines: [], support: false }
      if (Array.isArray(error.lines)) {
        return { lines: error.lines, support: error.support === true }
      }
      // O interceptor já normalizou e pendurou o resultado no erro: as telas
      // podem seguir fazendo `this.error = err` sem mudar nada.
      const friendly = error.friendly
      if (friendly && Array.isArray(friendly.lines)) {
        return { lines: friendly.lines, support: friendly.support === true }
      }
      if (isTransportError(error)) {
        const normalized = normalizeError(error)
        return { lines: normalized.lines, support: normalized.support }
      }
      if (typeof error.message === 'string' && error.message !== '') {
        return { lines: [error.message], support: false }
      }
      return { lines: [], support: false }
    },
    /**
     * Cada linha vira uma lista de segmentos. O `{suporte}` é separado ANTES do
     * `interpolate`, senão ele seria removido junto com os placeholders sem
     * valor no contexto.
     */
    lines () {
      const result = []
      this.resolved.lines.forEach(line => {
        if (typeof line !== 'string') return
        const parts = interpolate(line.split(SUPPORT_PLACEHOLDER), this.context)
        const segments = []
        parts.forEach((text, i) => {
          if (i > 0) segments.push({ link: true })
          if (text !== '') segments.push({ link: false, text: text })
        })
        if (segments.length > 0) result.push(segments)
      })
      return result
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Common/ErrorAlert.vue